import api from './api';

import type {
  User,
  UserActivityEventItem,
  UserActivitySummary,
  UserWithGroups,
} from '../types';


export interface UserActivitiesListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  user?: string;
  category?: string[];
  action?: string[];
  startDate?: string;
  endDate?: string;
}

export interface UserActivityListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserActivityEventItem[];
  summary: UserActivitySummary | null;
}

export interface UserOption {
  id: string;
  label: string;
  email: string;
}

export type MyTelephonyUnavailabilityReason = 'break' | 'lunch';

function buildUserOption(user: UserWithGroups): UserOption {
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return {
    id: user.id,
    label: name || user.email,
    email: user.email,
  };
}

export async function fetchUserActivities(
  params: UserActivitiesListParams = {},
): Promise<UserActivityListResponse> {
  const query: Record<string, unknown> = {};
  if (params.page !== undefined) {
    query.page = params.page;
  }
  if (params.pageSize !== undefined) {
    query.page_size = params.pageSize;
  }
  if (params.ordering) {
    query.ordering = params.ordering;
  }
  if (params.user) {
    query.user = params.user;
  }
  if (params.category && params.category.length > 0) {
    query.category = params.category.join(',');
  }
  if (params.action && params.action.length > 0) {
    query.action = params.action.join(',');
  }
  if (params.startDate) {
    query.start_date = params.startDate;
  }
  if (params.endDate) {
    query.end_date = params.endDate;
  }

  const response = await api.get('/api/users/activity/', { params: query });
  const payload = response.data as {
    count?: number;
    next?: string | null;
    previous?: string | null;
    results?: UserActivityEventItem[];
    summary?: UserActivitySummary | null;
  };
  return {
    count: payload.count ?? 0,
    next: payload.next ?? null,
    previous: payload.previous ?? null,
    results: payload.results ?? [],
    summary: payload.summary ?? null,
  };
}

export async function fetchUserOptions(params: {
  search?: string;
  selectedId?: string;
  limit?: number;
} = {}): Promise<UserOption[]> {
  const query: Record<string, unknown> = {
    page: 1,
    page_size: params.limit ?? 10,
    ordering: 'email',
  };
  if (params.search) {
    query.search = params.search;
  }

  const response = await api.get('/api/users/', { params: query });
  const results = Array.isArray(response.data?.results) ? response.data.results : [];
  const options = results.map((item: UserWithGroups) => buildUserOption(item));

  if (!params.selectedId || options.some((option: UserOption) => option.id === params.selectedId)) {
    return options;
  }

  try {
    const selectedResponse = await api.get(`/api/users/${params.selectedId}/`);
    const selectedOption = buildUserOption(selectedResponse.data as UserWithGroups);
    return [selectedOption, ...options];
  } catch (_error) {
    return options;
  }
}

export async function setMyTelephonyAvailabilityUnavailable(
  reason: MyTelephonyUnavailabilityReason,
): Promise<User> {
  const response = await api.post('/api/auth/me/telephony-availability/unavailable/', { reason });
  return response.data as User;
}

export async function setMyTelephonyAvailabilityAvailable(): Promise<User> {
  const response = await api.post('/api/auth/me/telephony-availability/available/');
  return response.data as User;
}
